import { MapPin, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { useLang } from '@/i18n/useLang';

interface LocationMapCardProps {
  city?: string | null;
  region?: string | null;
  country?: string | null;
}

const COPY = {
  ro: { open: 'Deschide', loading: 'Se încarcă harta…', title: 'Locație' },
  en: { open: 'Open',     loading: 'Loading map…',      title: 'Location' },
  de: { open: 'Öffnen',   loading: 'Karte wird geladen…', title: 'Standort' },
} as const;

const DEFAULT_COUNTRY: Record<string, string> = {
  ro: 'România',
  en: 'United Kingdom',
  de: 'Deutschland',
};

/**
 * Afișează locația vehiculului cu o hartă Google Maps integrată (fără API key — embed public).
 * Plasare inteligentă: în coloana laterală, sub cardul de dealer.
 */
export const LocationMapCard = ({ city, region, country }: LocationMapCardProps) => {
  const [loaded, setLoaded] = useState(false);
  const lang = useLang();
  const t = COPY[lang];
  const fallbackCountry = country || DEFAULT_COUNTRY[lang];

  const locationText = [city, region, fallbackCountry].filter(Boolean).join(', ');
  if (!city && !region) return null;

  const query = encodeURIComponent(locationText);
  const embedUrl = `https://www.google.com/maps?q=${query}&z=11&output=embed`;
  const openUrl = `https://www.google.com/maps/search/?api=1&query=${query}`;

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2 min-w-0">
          <MapPin className="h-4 w-4 text-primary shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-foreground truncate">{city || region}</div>
            {city && region && (
              <div className="text-xs text-muted-foreground truncate">{region}</div>
            )}
          </div>
        </div>
        <a
          href={openUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-primary hover:underline inline-flex items-center gap-1 shrink-0"
        >
          {t.open} <ExternalLink className="h-3 w-3" />
        </a>
      </div>
      <div className="relative aspect-[16/10] bg-muted">
        {!loaded && (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">
            {t.loading}
          </div>
        )}
        <iframe
          title={`${t.title}: ${locationText}`}
          src={embedUrl}
          className="absolute inset-0 w-full h-full border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          onLoad={() => setLoaded(true)}
        />
      </div>
    </div>
  );
};
